<template>
  <div class="facility-analytics">
    <div class="card shadow-sm table-card mb-4">
      <div class="form-card-header flex-wrap">
        <div class="section-number"><i class="fe fe-activity" style="font-size: 12px;"></i></div>
        <div class="flex-grow-1">
          <h6 class="mb-0 font-weight-bold text-white">Performance{{ d ? ` · ${d.facility.name}` : "" }}</h6>
          <small style="color: rgba(255,255,255,.75);" v-if="d">Ranked against {{ d.rank.of }} facilities in the agency for the same period</small>
        </div>
        <PeriodPicker v-model="period" @change="load" default-preset="90d" />
      </div>
      <div class="card-body">
        <div v-if="loading && !d" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
        <div v-else-if="error" class="empty-state"><i class="fe fe-alert-triangle"></i>{{ error }}</div>
        <template v-else-if="d">
          <div class="row">
            <div class="col-lg-3 col-md-6" v-for="tile in tiles" :key="tile.label">
              <div class="stat-card">
                <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
                <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3><p class="stat-sub">{{ tile.sub }}</p></div>
              </div>
            </div>
          </div>
          <apexchart type="line" height="280" :options="trendOptions" :series="trendSeries" :key="'t' + chartKey" />
        </template>
      </div>
    </div>

    <div class="row" v-if="d">
      <div class="col-lg-6">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header"><div class="section-number"><i class="fe fe-thermometer" style="font-size: 12px;"></i></div><h6 class="mb-0 font-weight-bold text-white">Diagnoses seen</h6></div>
          <div class="card-body">
            <apexchart v-if="d.diagnoses.length" type="bar" height="280" :options="dxOptions" :series="[{ name: 'Encounters', data: d.diagnoses.map((x) => x.encounters) }]" :key="'dx' + chartKey" />
            <div v-else class="empty-state">No encounters with a diagnosis in this period.</div>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header"><div class="section-number"><i class="fe fe-star" style="font-size: 12px;"></i></div>
            <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Ratings by enrollees</h6>
              <small style="color: rgba(255,255,255,.75);">{{ m.rating === null ? "No ratings in this period" : `${m.rating} ★ from ${m.ratings_count} ratings · agency average ${d.agency_average.rating === null ? "—" : d.agency_average.rating + " ★"}` }}</small></div></div>
          <div class="card-body">
            <apexchart type="bar" height="280" :options="ratingOptions" :series="[{ name: 'Ratings', data: [5, 4, 3, 2, 1].map((s) => d.ratings[s] || 0) }]" :key="'r' + chartKey" />
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header"><div class="section-number"><i class="fe fe-log-out" style="font-size: 12px;"></i></div>
            <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Referred out to</h6>
              <small style="color: rgba(255,255,255,.75);">{{ m.referrals_out }} referrals{{ m.referrals_out_per_1000_encounters === null ? "" : ` · ${m.referrals_out_per_1000_encounters} per 1,000 encounters` }}</small></div></div>
          <table class="table table-sm mb-0">
            <thead class="table-head"><tr><th class="table-th">Facility</th><th class="table-th text-right">Referrals</th><th class="table-th text-right">Approved</th></tr></thead>
            <tbody>
              <tr v-if="!d.referrals_out_to.length"><td colspan="3" class="empty-state">No referrals out.</td></tr>
              <tr class="table-row" v-for="r in d.referrals_out_to" :key="r.facility_id"><td><router-link :to="`/provider-${r.facility_id}`">{{ r.name || "Facility " + r.facility_id }}</router-link></td><td class="text-right">{{ r.count }}</td><td class="text-right">{{ r.approved }}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header"><div class="section-number"><i class="fe fe-log-in" style="font-size: 12px;"></i></div>
            <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Referrals received from</h6>
              <small style="color: rgba(255,255,255,.75);">{{ m.referrals_in }} received · {{ m.referrals_in_taken_up }} taken up{{ m.referral_uptake_pct === null ? "" : ` (${m.referral_uptake_pct}% of approved)` }}</small></div></div>
          <table class="table table-sm mb-0">
            <thead class="table-head"><tr><th class="table-th">Facility</th><th class="table-th text-right">Referrals</th><th class="table-th text-right">Taken up</th></tr></thead>
            <tbody>
              <tr v-if="!d.referrals_in_from.length"><td colspan="3" class="empty-state">No referrals received.</td></tr>
              <tr class="table-row" v-for="r in d.referrals_in_from" :key="r.facility_id"><td><router-link :to="`/provider-${r.facility_id}`">{{ r.name || "Facility " + r.facility_id }}</router-link></td><td class="text-right">{{ r.count }}</td><td class="text-right">{{ r.taken_up }}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <ForecastPanel v-if="d" fixed-metric="encounters" :facility-id="facilityId" compact />
  </div>
</template>

<script>
import PeriodPicker from "./PeriodPicker.vue";
import ForecastPanel from "./ForecastPanel.vue";

/** One facility's dashboard (GET analytics/facilities/{id}): KPIs with ranks, trend, diagnoses, ratings, referrals, forecast. */
export default {
  name: "FacilityAnalytics",
  components: { PeriodPicker, ForecastPanel },
  props: { facilityId: { type: [Number, String], required: true } },
  data() {
    return { period: { from: "", to: "" }, d: null, loading: false, error: "", chartKey: 0 };
  },
  computed: {
    m() { return this.d.metrics; },
    tiles() {
      const m = this.m;
      const r = this.d.rank;
      const rank = (n) => (n ? `#${n} of ${r.of}` : "—");
      const money = (v) => "₦" + Math.round(v || 0).toLocaleString();
      return [
        { label: "Enrollees assigned", value: m.enrollees.toLocaleString(), sub: `${m.enrollees_principals.toLocaleString()} principals, ${m.enrollees_dependants.toLocaleString()} dependants`, icon: "fe fe-users", bg: "#eff6ff", color: "#2563eb" },
        { label: "Encounters", value: m.encounters.toLocaleString(), sub: `${m.patients_seen.toLocaleString()} patients · ${rank(r.encounters)}`, icon: "fe fe-activity", bg: "#ecfdf5", color: "#059669" },
        { label: "Per 1,000 enrollees", value: m.encounters_per_1000 === null ? "—" : m.encounters_per_1000, sub: `agency ${this.d.agency_average.encounters_per_1000 ?? "—"} · ${rank(r.encounters_per_1000)}`, icon: "fe fe-percent", bg: "#f5f3ff", color: "#7c3aed" },
        { label: "Enrollees seen", value: m.patients_seen_pct === null ? "—" : m.patients_seen_pct + "%", sub: "at least once in the period", icon: "fe fe-user-check", bg: "#f0fdfa", color: "#0d9488" },
        { label: "Authorization codes", value: m.codes_requested.toLocaleString(), sub: `${m.codes_approved} approved, ${m.codes_rejected} declined, ${m.codes_pending} pending`, icon: "fe fe-shield", bg: "#fdf4ff", color: "#a21caf" },
        { label: "Claims", value: m.claims_submitted.toLocaleString(), sub: `${money(m.amount_approved)} approved of ${money(m.amount_claimed)} · ${rank(r.claims_submitted)}`, icon: "fe fe-file-text", bg: "#fdf2f8", color: "#db2777" },
        { label: "Referrals", value: `${m.referrals_out} out / ${m.referrals_in} in`, sub: `${m.referrals_in_taken_up} incoming taken up`, icon: "fe fe-share-2", bg: "#fff7ed", color: "#ea580c" },
        { label: "Rating", value: m.rating === null ? "—" : `${m.rating} ★`, sub: `${m.ratings_count} ratings · ${rank(r.rating)}`, icon: "fe fe-star", bg: "#fefce8", color: "#ca8a04" },
      ];
    },
    trendSeries() {
      const s = this.d.trend.series;
      return [{ name: "Encounters", data: s.encounters }, { name: "Authorization codes", data: s.authorization_codes }, { name: "Claims", data: s.claims }, { name: "Referrals received", data: s.referrals_in }];
    },
    trendOptions() {
      return {
        chart: { toolbar: { show: true } }, colors: ["#059669", "#7c3aed", "#db2777", "#ea580c"], stroke: { width: 3, curve: "smooth" },
        xaxis: { categories: this.d.trend.categories, labels: { rotate: -45, hideOverlappingLabels: true } }, dataLabels: { enabled: true },
        legend: { position: "top" }, tooltip: { shared: true, intersect: false },
      };
    },
    dxOptions() {
      const list = this.d.diagnoses;
      return {
        chart: { toolbar: { show: false } }, colors: ["#0891b2"], plotOptions: { bar: { horizontal: true, barHeight: "70%", borderRadius: 3 } },
        xaxis: { categories: list.map((x) => x.name) }, dataLabels: { enabled: true, formatter: (v, o) => `${v} (${list[o.dataPointIndex].share_pct}%)`, style: { fontSize: "10px" } },
      };
    },
    ratingOptions() {
      return {
        chart: { toolbar: { show: false } }, colors: ["#ca8a04"], plotOptions: { bar: { horizontal: true, barHeight: "60%", borderRadius: 3 } },
        xaxis: { categories: ["5 ★", "4 ★", "3 ★", "2 ★", "1 ★"] }, dataLabels: { enabled: true },
      };
    },
  },
  watch: { facilityId() { this.load(); } },
  mounted() { if (this.period.from) this.load(); },
  methods: {
    load() {
      if (!this.period.from || !this.facilityId) return;
      this.loading = true;
      this.error = "";
      this.$batchGet(`/api/v1/auth/analytics/facilities/${this.facilityId}`, { params: { from: this.period.from, to: this.period.to } })
        .then((res) => { this.d = res.data.data; this.chartKey++; })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load this facility's performance."; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
</style>
